// Helpers voor bulk-acties op geselecteerde documenten:
//   - downloadZip: pakt geselecteerde PDFs in één .zip via JSZip (client-side).
//   - exportXlsx:  fetcht volledige metadata per doc, schrijft een .xlsx via SheetJS.
//
// Beide werken volledig in de browser; geen extra backend-calls naast de
// reguliere `/api/documents/:id/pdf` en `/api/documents/:id` endpoints.

import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { api, documentPdfUrl, type DocumentDetail } from './api';

function triggerDownload(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

function timestampForFilename(d: Date = new Date()): string {
	const pad = (n: number) => String(n).padStart(2, '0');
	return (
		d.getFullYear().toString() +
		pad(d.getMonth() + 1) +
		pad(d.getDate()) +
		'-' +
		pad(d.getHours()) +
		pad(d.getMinutes())
	);
}

function safeFilename(name: string, fallback: string): string {
	const cleaned = name.replace(/[\\/:*?"<>|]+/g, '_').trim();
	return cleaned || fallback;
}

export interface SelectedDocSummary {
	id: string;
	original_filename: string | null;
	counterparty_name: string | null;
	document_date: string | null;
}

// --- ZIP -------------------------------------------------------------------

export async function downloadZip(docs: SelectedDocSummary[]): Promise<void> {
	const zip = new JSZip();
	const seen = new Map<string, number>();

	const fetches = docs.map(async (doc) => {
		const res = await fetch(documentPdfUrl(doc.id), { credentials: 'include' });
		if (!res.ok) throw new Error(`Bestand ${doc.id} ophalen mislukt (${res.status})`);
		const buf = await res.arrayBuffer();

		// Bestandsnaam in de ZIP: <datum> - <afzender> - <origineel-met-extensie>
		const orig = doc.original_filename ?? `${doc.id}`;
		const extMatch = /\.[a-z0-9]{2,5}$/i.exec(orig);
		const ext = extMatch ? extMatch[0] : '';
		const base = safeFilename(
			[doc.document_date ?? '', doc.counterparty_name ?? '', orig].filter(Boolean).join(' - '),
			`${doc.id}${ext}`,
		);
		const count = (seen.get(base) ?? 0) + 1;
		seen.set(base, count);
		// Voorkom dubbele namen door volgnummer net voor de extensie te plakken.
		const name =
			count === 1
				? base
				: ext
				? base.replace(new RegExp(`${ext.replace('.', '\\.')}$`, 'i'), ` (${count})${ext}`)
				: `${base} (${count})`;

		zip.file(name, buf);
	});

	await Promise.all(fetches);

	const blob = await zip.generateAsync({ type: 'blob' });
	triggerDownload(blob, `docr-export-${timestampForFilename()}.zip`);
}

// --- XLSX ------------------------------------------------------------------
//
// Per kolom een type:
//   string  → tekst-cel
//   number  → numerieke cel met format `#,##0.00` (NL Excel toont ", " decimaal)
//   percent → numerieke cel 0..1 met format `0.00%`
//   date    → datum-cel met format `dd-mm-yyyy`
//
// Adresvelden (counterparty_address en addressee) worden gesplitst in
// straat+nr / postcode / plaats via een eenvoudige Nederlandse-postcode-parser.

type ColumnKind = 'string' | 'number' | 'percent' | 'date' | 'datetime' | 'link';
type ColumnSpec = {
	header: string;
	kind: ColumnKind;
	get: (doc: DocumentDetail) => string | number | Date | null;
};

const NL_POSTCODE_RE = /\b(\d{4})\s*([A-Z]{2})\b\s*(.+)/;

interface ParsedAddress {
	name: string | null; // alleen relevant voor `addressee`; bij counterparty leeg
	street: string | null;
	postcode: string | null;
	city: string | null;
}

function parseDutchAddress(raw: string | null): ParsedAddress {
	if (!raw) return { name: null, street: null, postcode: null, city: null };
	const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
	let postcodeIdx = -1;
	let postcode: string | null = null;
	let city: string | null = null;
	for (let i = 0; i < lines.length; i++) {
		const m = NL_POSTCODE_RE.exec(lines[i]);
		if (m) {
			postcodeIdx = i;
			postcode = `${m[1]} ${m[2]}`;
			city = m[3].trim();
			break;
		}
	}
	if (postcodeIdx === -1) {
		// Geen NL-postcode gevonden: hele tekst in straat-veld als beste-gok.
		return { name: null, street: raw.replace(/\n+/g, ', '), postcode: null, city: null };
	}
	const street = postcodeIdx > 0 ? lines[postcodeIdx - 1] : null;
	const namesBefore = lines.slice(0, Math.max(0, postcodeIdx - 1));
	return {
		name: namesBefore.length > 0 ? namesBefore.join('\n') : null,
		street,
		postcode,
		city,
	};
}

function parseDateOnly(s: string | null): Date | null {
	if (!s) return null;
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
	if (!m) return null;
	// UTC-noon zodat de datum niet door tijdzones één dag opschuift.
	return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0));
}

function parseNumberOrNull(s: string | number | null | undefined): number | null {
	if (s === null || s === undefined || s === '') return null;
	if (typeof s === 'number') return Number.isFinite(s) ? s : null;
	const n = Number(String(s).replace(',', '.'));
	return Number.isFinite(n) ? n : null;
}

// Canonieke productie-URL — niet afhankelijk van waar de export wordt gemaakt
// (preview-deploy zou anders preview-links opleveren in een gedeeld bestand).
const DOCR_BASE_URL = 'https://app.docr.nl';

const XLSX_COLUMNS: ColumnSpec[] = [
	{ header: 'document_id', kind: 'string', get: (d) => d.id },
	{ header: 'docr_link', kind: 'link', get: (d) => `${DOCR_BASE_URL}/documents/${d.id}` },
	{ header: 'entiteit', kind: 'string', get: (d) => `${d.entity_code} — ${d.entity_name}` },
	{ header: 'type', kind: 'string', get: (d) => d.document_type },
	{ header: 'richting', kind: 'string', get: (d) => d.direction },
	{ header: 'documentdatum', kind: 'date', get: (d) => parseDateOnly(d.document_date) },
	{ header: 'vervaldatum', kind: 'date', get: (d) => parseDateOnly(d.due_date) },
	{ header: 'afzender', kind: 'string', get: (d) => d.counterparty_name },
	// Adres-splitsing: counterparty_address heeft géén naam-regel.
	{ header: 'afzender_straat', kind: 'string', get: (d) => parseDutchAddress(d.counterparty_address).street },
	{ header: 'afzender_postcode', kind: 'string', get: (d) => parseDutchAddress(d.counterparty_address).postcode },
	{ header: 'afzender_plaats', kind: 'string', get: (d) => parseDutchAddress(d.counterparty_address).city },
	{ header: 'afzender_telefoon', kind: 'string', get: (d) => d.counterparty_phone },
	{ header: 'afzender_email', kind: 'string', get: (d) => d.counterparty_email },
	{ header: 'afzender_website', kind: 'string', get: (d) => d.counterparty_website },
	{ header: 'afzender_btw', kind: 'string', get: (d) => d.counterparty_vat },
	{ header: 'afzender_kvk', kind: 'string', get: (d) => d.counterparty_kvk },
	{ header: 'afzender_iban', kind: 'string', get: (d) => d.counterparty_iban },
	// Geadresseerde: alle regels VÓÓR de straat-regel = naam (incl. eventuele
	// vertegenwoordiger / functietitel). Daarna de drie adres-onderdelen.
	{ header: 'geadresseerde_naam', kind: 'string', get: (d) => parseDutchAddress(d.addressee).name },
	{ header: 'geadresseerde_straat', kind: 'string', get: (d) => parseDutchAddress(d.addressee).street },
	{ header: 'geadresseerde_postcode', kind: 'string', get: (d) => parseDutchAddress(d.addressee).postcode },
	{ header: 'geadresseerde_plaats', kind: 'string', get: (d) => parseDutchAddress(d.addressee).city },
	{ header: 'referentie', kind: 'string', get: (d) => d.reference },
	{ header: 'referentie_type', kind: 'string', get: (d) => d.reference_type },
	{ header: 'totaal', kind: 'number', get: (d) => parseNumberOrNull(d.amount_total) },
	{ header: 'excl_btw', kind: 'number', get: (d) => parseNumberOrNull(d.amount_excl_vat) },
	{ header: 'btw_bedrag', kind: 'number', get: (d) => parseNumberOrNull(d.vat_amount) },
	{ header: 'valuta', kind: 'string', get: (d) => d.currency },
	{ header: 'onderwerp', kind: 'string', get: (d) => d.subject },
	{ header: 'samenvatting', kind: 'string', get: (d) => d.summary },
	{ header: 'tags', kind: 'string', get: (d) => (Array.isArray(d.tags) ? d.tags.join(', ') : d.tags) },
	{ header: 'opmerking', kind: 'string', get: (d) => d.notes },
	{ header: 'status', kind: 'string', get: (d) => d.status },
	{ header: 'review_nodig', kind: 'string', get: (d) => (d.needs_review ? 'ja' : 'nee') },
	{ header: 'confidence', kind: 'percent', get: (d) => parseNumberOrNull(d.confidence_overall) },
	{ header: 'bestandsnaam', kind: 'string', get: (d) => d.original_filename },
	{ header: 'aangekomen', kind: 'datetime', get: (d) => (d.created_at ? new Date(d.created_at) : null) },
	{ header: 'verwerkt', kind: 'datetime', get: (d) => (d.processed_at ? new Date(d.processed_at) : null) },
	{ header: 'mail_van', kind: 'string', get: (d) => d.source_email_from },
	{ header: 'mail_onderwerp', kind: 'string', get: (d) => d.source_email_subject },
];

const FORMAT_NUMBER = '#,##0.00';
const FORMAT_PERCENT = '0.00%';
const FORMAT_DATE = 'dd-mm-yyyy';
// `hh:mm` zonder am/pm = 24-uurs in Excel.
const FORMAT_DATETIME = 'dd-mm-yyyy hh:mm';

function buildCell(value: string | number | Date | null, kind: ColumnKind): XLSX.CellObject | null {
	if (value === null || value === undefined || value === '') return null;
	if (kind === 'number') {
		if (typeof value !== 'number') return null;
		return { t: 'n', v: value, z: FORMAT_NUMBER };
	}
	if (kind === 'percent') {
		if (typeof value !== 'number') return null;
		return { t: 'n', v: value, z: FORMAT_PERCENT };
	}
	if (kind === 'date' || kind === 'datetime') {
		if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
		return { t: 'd', v: value, z: kind === 'datetime' ? FORMAT_DATETIME : FORMAT_DATE };
	}
	if (kind === 'link') {
		if (typeof value !== 'string' || !value) return null;
		// Hyperlink-cel: zichtbare tekst is de URL zelf, klik opent het in Excel.
		return { t: 's', v: value, l: { Target: value, Tooltip: 'Open document in DocR' } };
	}
	// string
	const s = typeof value === 'string' ? value : String(value);
	return { t: 's', v: s };
}

export async function exportXlsx(documentIds: string[]): Promise<void> {
	// Volledige detail per doc parallel ophalen.
	const details = await Promise.all(documentIds.map((id) => api.document(id).then((r) => r.document)));

	const headerRow: XLSX.CellObject[] = XLSX_COLUMNS.map((c) => ({ t: 's', v: c.header }));
	const dataRows: Array<Array<XLSX.CellObject | null>> = details.map((doc) =>
		XLSX_COLUMNS.map((col) => buildCell(col.get(doc), col.kind)),
	);

	// aoa_to_sheet met cellDates accepteert ook onze eigen CellObjects als 1e-kolom-elementen
	const aoa: Array<Array<XLSX.CellObject | null>> = [headerRow, ...dataRows];
	const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });

	// Auto-width: per kolom max van header- en celtekstlengte (gecapped op 60).
	ws['!cols'] = XLSX_COLUMNS.map((c, i) => {
		const max = dataRows.reduce<number>((m, row) => {
			const cell = row[i];
			const s = cell?.v == null ? '' : cell.t === 'd' ? '10' : String(cell.v);
			return Math.max(m, s.length);
		}, c.header.length);
		return { wch: Math.min(60, max + 2) };
	});

	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, 'documenten');

	const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx', cellDates: true });
	const blob = new Blob([out], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});
	triggerDownload(blob, `docr-export-${timestampForFilename()}.xlsx`);
}

